import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Loader2, TrendingUp, TrendingDown, DollarSign, Users, AlertCircle, Award, RefreshCw } from 'lucide-react';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine, LabelList } from 'recharts';
import { toast } from 'sonner';

const fmt$ = (n) => `$${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

const RetentionTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  return (
    <div className="bg-white border rounded shadow-sm p-2 text-xs">
      <div className="font-semibold">{d.year} cohort</div>
      <div>{d.acquired} donor{d.acquired === 1 ? '' : 's'} acquired this year</div>
      <div>{d.retained} gave again the following year</div>
      <div className="font-medium text-[#6B3FA0]">{d.rate}% retention</div>
    </div>
  );
};

/**
 * Combined Overview tab (formerly Dashboard + Reports).
 * Shows: KPIs, LYBUNT/SYBUNT, retention chart, campaign ROI, frequency
 * segmentation, recent activity feed.
 */
export function OverviewTab({ api }) {
  const [summary, setSummary] = useState(null);
  const [lybunt, setLybunt] = useState(null);
  const [sybunt, setSybunt] = useState(null);
  const [retention, setRetention] = useState(null);
  const [camps, setCamps] = useState(null);
  const [freq, setFreq] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, l, y, r, c, f] = await Promise.all([
        api.get('/donors/reports/summary'),
        api.get('/donors/reports/lybunt'),
        api.get('/donors/reports/sybunt'),
        api.get('/donors/reports/retention'),
        api.get('/donors/reports/campaign-roi'),
        api.get('/donors/reports/frequency'),
      ]);
      setSummary(s.data); setLybunt(l.data); setSybunt(y.data);
      setRetention(r.data); setCamps(c.data); setFreq(f.data);
    } catch { toast.error('Failed to load overview'); }
    setLoading(false);
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const retentionSeries = useMemo(() => {
    if (!retention?.cohorts) return [];
    // Compute YoY retention rate for the most recent 5 cohort years
    return retention.cohorts.slice(-6).map(c => {
      const yrs = c.years || {};
      const acq = c.acquired || 1;
      const nextYear = String(c.cohort_year + 1);
      const rate = yrs[nextYear] ? Math.round((yrs[nextYear] / acq) * 100) : 0;
      return { year: c.cohort_year, acquired: acq, retained: yrs[nextYear] || 0, rate };
    });
  }, [retention]);

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="h-7 w-7 animate-spin text-[#6B3FA0]" /></div>;

  return (
    <div className="space-y-4" data-testid="overview-tab">
      {/* KPI row */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <Kpi icon={DollarSign} label={`${summary.year} raised`} value={fmt$(summary.ytd_total)} testid="kpi-ytd" />
          <Kpi icon={Award} label={`${summary.year} gifts`} value={summary.ytd_count} testid="kpi-gifts" />
          <Kpi icon={Users} label="Active donors" value={summary.pipeline?.donor || 0} testid="kpi-active" />
          <Kpi icon={AlertCircle} label="Unacknowledged" value={summary.unacknowledged_gifts}
               variant={summary.unacknowledged_gifts > 0 ? 'warn' : 'default'} testid="kpi-unacked" />
          <Kpi icon={TrendingDown} label="LYBUNT" value={lybunt?.count || 0}
               variant={(lybunt?.count || 0) > 0 ? 'warn' : 'default'} testid="kpi-lybunt" />
        </div>
      )}

      {/* Two-column: retention chart + campaign ROI */}
      <div className="grid md:grid-cols-2 gap-4">
        {/* Pipeline funnel */}
        {summary?.pipeline && (
          <Card>
            <CardContent className="p-4">
              <div className="font-semibold mb-3">Pipeline funnel</div>
              <div className="space-y-1.5">
                {['prospect', 'cultivating', 'solicited', 'donor', 'steward', 'lapsed'].map(stage => {
                  const count = summary.pipeline[stage] || 0;
                  const total = Object.values(summary.pipeline).reduce((s, v) => s + (v || 0), 0) || 1;
                  const pct = Math.round((count / total) * 100);
                  return (
                    <div key={stage} className="flex items-center gap-2 text-sm" data-testid={`pipeline-${stage}`}>
                      <span className="w-24 capitalize text-gray-600">{stage}</span>
                      <div className="flex-1 h-5 bg-gray-100 rounded overflow-hidden relative">
                        <div className="absolute inset-y-0 left-0 bg-[#6B3FA0]" style={{ width: `${pct}%` }} />
                        <span className="absolute inset-0 flex items-center px-2 text-xs font-medium">{count} · {pct}%</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="font-semibold flex items-center gap-2"><TrendingUp className="h-4 w-4 text-[#6B3FA0]" /> Year-over-year retention</div>
              <Button size="sm" variant="ghost" onClick={load} data-testid="overview-refresh"><RefreshCw className="h-3.5 w-3.5" /></Button>
            </div>
            {retentionSeries.length === 0 ? <p className="text-sm text-gray-500">Not enough historical data yet.</p> : (
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={retentionSeries} margin={{ top: 20, right: 6, left: 6, bottom: 0 }}>
                  <XAxis dataKey="year" fontSize={11} />
                  <YAxis fontSize={11} unit="%" domain={[0, 100]} />
                  <Tooltip content={<RetentionTooltip />} />
                  <ReferenceLine y={50} stroke="#eee" strokeDasharray="3 3" />
                  <Bar dataKey="rate" fill="#6B3FA0" radius={[4, 4, 0, 0]}>
                    <LabelList dataKey="acquired" position="top" fontSize={10}
                               formatter={(v) => `${v} donor${v === 1 ? '' : 's'}`} fill="#6B3FA0" />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
            <p className="text-xs text-gray-500 mt-2">
              Bar height = % of that year&apos;s new donors who gave again the next year. Label above each bar = number of donors first acquired that year. If a year surprises you, someone likely imported gifts with a 2-digit-year date (e.g. <code>1/15/24</code> parses as 2024).
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="font-semibold mb-3">Campaign ROI</div>
            {!camps || camps.length === 0 ? <p className="text-sm text-gray-500">No gifts yet. Log a few to see this chart.</p> : (
              <>
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={camps.slice(0, 8)} layout="vertical" margin={{ top: 0, right: 6, left: 8, bottom: 0 }}>
                    <XAxis type="number" fontSize={11} tickFormatter={fmt$} />
                    <YAxis type="category" dataKey="campaign" fontSize={11} width={110} />
                    <Tooltip formatter={(v) => fmt$(v)} />
                    <Bar dataKey="raised" fill="#8B5CF6" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
                <div className="text-xs text-gray-500 mt-2 grid grid-cols-3 gap-2">
                  {camps.slice(0, 3).map(c => (
                    <div key={c.campaign} className="border rounded p-1.5">
                      <div className="font-medium truncate">{c.campaign}</div>
                      <div>{c.donor_count} donors · avg {fmt$(c.avg_gift)}</div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Top donors YTD */}
        {summary?.top_donors && summary.top_donors.length > 0 && (
          <Card>
            <CardContent className="p-4">
              <div className="font-semibold mb-3 flex items-center gap-2"><Award className="h-4 w-4 text-[#6B3FA0]" /> Top donors this year</div>
              <div className="space-y-1">
                {summary.top_donors.slice(0, 10).map((d, i) => (
                  <div key={d.donor_id} className="flex justify-between text-sm border-b pb-1 last:border-0" data-testid={`top-donor-${i}`}>
                    <span className="truncate"><span className="text-gray-400 tabular-nums mr-2">{i + 1}.</span>{d.donor_name || '(no name)'}</span>
                    <span className="tabular-nums font-medium">{fmt$(d.total)}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* LYBUNT / SYBUNT lists */}
      <div className="grid md:grid-cols-2 gap-4">
        <ReactivationList title="LYBUNT — gave last year, not this" data={lybunt} amountKey="last_year_total" testidPrefix="lybunt" />
        <ReactivationList title="SYBUNT — gave before, not this year" data={sybunt} amountKey="lifetime_total" testidPrefix="sybunt" />
      </div>

      {/* Frequency segmentation */}
      {freq && (
        <Card>
          <CardContent className="p-4">
            <div className="font-semibold mb-3">Donor frequency segments</div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {freq.segments.map(seg => (
                <div key={seg.bucket} className="border rounded p-3" data-testid={`freq-seg-${seg.bucket.replace(/[^a-z0-9]/gi, '')}`}>
                  <div className="text-xs uppercase text-gray-500">{seg.bucket === '1' ? 'One-time' : `${seg.bucket} gifts`}</div>
                  <div className="text-xl font-bold mt-0.5">{seg.donor_count}</div>
                  <div className="text-xs text-gray-500">{fmt$(seg.total_raised)} raised</div>
                  <div className="mt-2 space-y-0.5">
                    {seg.top_5.slice(0, 3).map(t => (
                      <div key={t.donor_id} className="text-[11px] flex justify-between text-gray-600">
                        <span className="truncate">{t.name}</span>
                        <span className="tabular-nums">{fmt$(t.total)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function Kpi({ icon: Icon, label, value, variant, testid }) {
  return (
    <Card data-testid={testid}>
      <CardContent className={`p-3 ${variant === 'warn' ? 'bg-amber-50' : ''}`}>
        <div className="flex items-center gap-2 text-xs text-gray-500 uppercase">
          <Icon className={`h-3.5 w-3.5 ${variant === 'warn' ? 'text-amber-600' : 'text-[#6B3FA0]'}`} /> {label}
        </div>
        <div className="text-2xl font-bold mt-1">{value}</div>
      </CardContent>
    </Card>
  );
}

function ReactivationList({ title, data, amountKey, testidPrefix }) {
  const [showAll, setShowAll] = useState(false);
  if (!data) return null;
  const rows = (data.donors || []);
  const visible = showAll ? rows : rows.slice(0, 6);
  return (
    <Card data-testid={`${testidPrefix}-card`}>
      <CardContent className="p-4">
        <div className="font-semibold mb-2 flex items-center justify-between">
          <span>{title}</span>
          <Badge className="bg-amber-100 text-amber-800 border-0">{data.count}</Badge>
        </div>
        {rows.length === 0 ? <p className="text-sm text-gray-500">None — 🎉</p> : (
          <>
            <div className="space-y-1">
              {visible.map(d => (
                <div key={d.donor_id} className="flex justify-between text-sm border-b pb-1 last:border-0">
                  <span className="truncate">{d.donor_name || '(no name)'}</span>
                  <span className="tabular-nums font-medium">{fmt$(d[amountKey])}</span>
                </div>
              ))}
            </div>
            {rows.length > 6 && (
              <Button variant="link" size="sm" onClick={() => setShowAll(x => !x)} className="mt-1 h-auto p-0">
                {showAll ? 'Show less' : `Show all ${rows.length}`}
              </Button>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
